import { FC } from "react";
import { ShoppingCart } from "@mui/icons-material";
import { useAccount } from "wagmi";
import { stringToHex } from "viem";
import { useAppKit } from "@reown/appkit/react";

import { useUser } from "@dzambalaorg/provider-user";
import {
  useAllowance,
  useMetamask,
  useServerSignature,
  useWriteContractWithSimulation,
} from "@dzambalaorg/react-hooks-eth";
import type { IServerSignature } from "@dzambalaorg/types-blockchain";
import {
  convertDatabaseAssetToChainAsset,
  convertDatabaseAssetToTokenTypeAsset,
  convertTemplateToChainAsset,
  getEthPrice,
} from "@framework/exchange";
import { ListAction, ListActionVariant } from "@framework/styled";
import {
  AddressType,
  ContractFeatures,
  type IContract,
  type ITemplate,
  type IUser,
  TemplateStatus,
  TokenType,
} from "@framework/types";
import ExchangePurchaseFacetPurchaseABI from "@framework/abis/json/ExchangePurchaseFacet/purchase.json";
import { ZeroAddress } from "@framework/constants";
import { useGlobalDialogFormCallbacks } from "@framework/shared";
import { IOnConfirmArgs } from "@dzambalaorg/react-hooks";

import { MarketDialogKeys } from "../../../../../shared";
import { IAmountDto, amountValidationSchema } from "./dialog";

interface ITemplatePurchaseButtonProps {
  className?: string;
  disabled?: boolean;
  template: ITemplate;
  variant?: ListActionVariant;
}

export const TemplatePurchaseButton: FC<ITemplatePurchaseButtonProps> = props => {
  const { className, disabled, template, variant = ListActionVariant.button } = props;

  const { isConnected } = useAccount();
  const { writeContractAsync } = useWriteContractWithSimulation();
  const { open } = useAppKit();
  const user = useUser<IUser>();
  const isUserAuthenticated = user.isAuthenticated();

  const { openDialog } = useGlobalDialogFormCallbacks<ITemplate, IAmountDto>();

  const { execute: metaFnWithAllowance } = useAllowance(
    async (values: IAmountDto, sign: IServerSignature, systemContract: IContract) => {
      const item = convertTemplateToChainAsset(template, values.amount);
      const price = convertDatabaseAssetToChainAsset(template.price?.components, { multiplier: values.amount });

      return writeContractAsync({
        address: systemContract.address as AddressType,
        abi: ExchangePurchaseFacetPurchaseABI,
        functionName: "purchase",
        args: [
          {
            externalId: BigInt(template.id),
            expiresAt: BigInt(sign.expiresAt),
            nonce: sign.nonce,
            extra: stringToHex("0x", { size: 32 }),
            receiver: template.contract!.merchant!.wallet,
            referrer: ZeroAddress,
          },
          item,
          price,
          sign.signature,
        ],
        value: getEthPrice(template.price) * BigInt(values.amount),
      });
    },
  );

  const metaFnWithSign = useServerSignature((values: IAmountDto, sign: IServerSignature, systemContract: IContract) => {
    const assets = convertDatabaseAssetToTokenTypeAsset(template.price?.components, {
      multiplier: values.amount,
    });
    return metaFnWithAllowance(
      {
        contract: systemContract.address,
        assets,
      },
      values,
      sign,
      systemContract,
    );
  });

  const { execute: metaFn } = useMetamask((values: IAmountDto) => {
    return metaFnWithSign(
      {
        url: "/marketplace/sign",
        method: "POST",
        data: {
          referrer: ZeroAddress,
          templateId: template.id,
          amount: values.amount,
        },
      },
      values,
    ) as Promise<void>;
  });

  const handleConnect = () => {
    if (open) {
      void open();
    }
  };

  const handleAmountConfirm = async ({ data }: IOnConfirmArgs<IAmountDto>): Promise<void> => {
    await metaFn(data);
  };

  const handleBuy = async () => {
    if (template.contract!.contractType === TokenType.ERC1155) {
      openDialog(MarketDialogKeys.AMOUNT, template, {
        initialValues: {
          amount: 1,
        },
        validationSchema: amountValidationSchema,
        message: "dialogs.amount",
        testId: "AmountForm",
        onExternalConfirm: handleAmountConfirm,
        componentProps: {
          initialValues: {
            amount: 1,
          },
        },
      });
    } else {
      await metaFn({
        amount: 1,
      });
    }
  };

  if (template.contract?.contractFeatures.includes(ContractFeatures.EXTERNAL)) {
    return null;
  }

  if (template.templateStatus !== TemplateStatus.ACTIVE) {
    return null;
  }

  // Random contract must be registered in Chain-link VRF
  // TODO may-be hide BUY button completely, but it brakes formatting
  let rndBuyEnabled = true;
  if (
    (template.contract?.contractFeatures.includes(ContractFeatures.RANDOM) ||
      template.contract?.contractFeatures.includes(ContractFeatures.GENES)) &&
    (!template.contract?.parameters.vrfSubId || !template.contract?.parameters.isConsumer)
  ) {
    rndBuyEnabled = false;
  }

  return (
    <ListAction
      icon={ShoppingCart}
      onClick={isConnected && isUserAuthenticated ? handleBuy : handleConnect}
      message={isConnected && isUserAuthenticated ? "form.buttons.buy" : "components.header.wallet.connect"}
      className={className}
      dataTestId="TemplatePurchaseButton"
      disabled={disabled || !rndBuyEnabled || (template.cap !== "0" && BigInt(template.amount) >= BigInt(template.cap))}
      variant={variant}
    />
  );
};
